import React, { useState, useMemo, useEffect } from 'react';
import Swal from 'sweetalert2';
import Select from 'react-select';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

// Global admin route prefix (centralize adjustments if route group changes)
const ADMIN_PREFIX = '/admin';

const BatchGroups = () => {
    const [batchGroups, setBatchGroups] = useState([]);
    const [stats, setStats] = useState({ totalBatchGroups: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // State Management
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBatchGroup, setEditingBatchGroup] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [formData, setFormData] = useState({
        name: '',
        whatsappGroupName: '',
    });

    const loadBatchGroups = async () => {
        setLoading(true);
        setError('');
        try {
            const resp = await axiosInstance.get(`${ADMIN_PREFIX}/batch-groups`);
            const data = resp.data || {};
            setBatchGroups(data.batchGroups || []);
            if (data.stats) {
                setStats({
                    totalBatchGroups: data.stats.totalBatchGroups ?? (data.batchGroups || []).length,
                });
            } else {
                setStats({ totalBatchGroups: (data.batchGroups || []).length });
            }
        } catch (err) {
            console.error('Failed to load batch groups', err);
            setError(err.response?.data?.message || 'Failed to load batch groups');
            setBatchGroups([]);
            setStats({ totalBatchGroups: 0 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBatchGroups();
    }, []);

    // Filter and pagination logic (null-safe)
    const filteredBatchGroups = useMemo(() => {
        const term = (searchTerm || '').toLowerCase();
        if (!term) return batchGroups;

        return batchGroups.filter((group) => {
            const name = (group && group.name) ? String(group.name).toLowerCase() : '';
            const whatsapp = (group && group.whatsappGroupName) ? String(group.whatsappGroupName).toLowerCase() : '';
            return name.includes(term) || whatsapp.includes(term);
        });
    }, [batchGroups, searchTerm]);

    const totalPages = Math.ceil(filteredBatchGroups.length / itemsPerPage);
    const paginatedBatchGroups = filteredBatchGroups.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Form Management
    const resetForm = () => {
        setFormData({
            name: '',
            whatsappGroupName: '',
        });
        setEditingBatchGroup(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const openModal = (batchGroup) => {
        if (batchGroup) {
            setEditingBatchGroup(batchGroup);
            setFormData({
                name: batchGroup.name,
                whatsappGroupName: batchGroup.whatsappGroupName || '',
            });
        } else {
            resetForm();
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        resetForm();
    };

    // CRUD Operations
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingBatchGroup) {
                await axiosInstance.put(`${ADMIN_PREFIX}/batch-groups/${editingBatchGroup.batchGroupId}`, formData);
            } else {
                await axiosInstance.post(`${ADMIN_PREFIX}/batch-groups`, formData);
            }
            await loadBatchGroups();
            closeModal();
        } catch (error) {
            console.error('Error saving batch group:', error);
            Swal.fire('Error', 'Failed to save batch group', 'error');
        }
    };

    const handleDelete = async (batchGroup) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Do you want to delete the batch group "${batchGroup.name}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await axiosInstance.delete(`${ADMIN_PREFIX}/batch-groups/${batchGroup.batchGroupId}`);
                await loadBatchGroups();
                Swal.fire('Deleted!', 'Batch group deleted successfully', 'success');
            } catch (error) {
                console.error('Error deleting batch group:', error);
                Swal.fire('Error', 'Failed to delete batch group', 'error');
            }
        }
    };

    // Modal states
    const [batchesModalOpen, setBatchesModalOpen] = useState(false);
    const [subjectsModalOpen, setSubjectsModalOpen] = useState(false);
    const [selectedBatchGroup, setSelectedBatchGroup] = useState(null);
    const [batchesData, setBatchesData] = useState({ assignedBatches: [], availableBatches: [] });
    const [subjectsData, setSubjectsData] = useState({ assignedBatchSubjects: [], availableBatchSubjects: [] });

    // Navigation functions
    const handleManageBatches = async (batchGroup) => {
        setSelectedBatchGroup(batchGroup);
        try {
            const response = await axiosInstance.get(`${ADMIN_PREFIX}/batch-groups/${batchGroup.batchGroupId}/batches`, {
                params: { t: Date.now() },
            });
            const data = response.data || {};
            setBatchesData(data);
            setBatchesModalOpen(true);
        } catch (error) {
            console.error('Error loading batches data:', error);
            Swal.fire('Error', 'Failed to load batches data', 'error');
        }
    };

    const handleManageSubjects = async (batchGroup) => {
        setSelectedBatchGroup(batchGroup);
        try {
            const response = await axiosInstance.get(`${ADMIN_PREFIX}/batch-groups/${batchGroup.batchGroupId}/subjects`, {
                params: { t: Date.now() },
            });
            const data = response.data || {};
            setSubjectsData(data);
            setSubjectsModalOpen(true);
        } catch (error) {
            console.error('Error loading subjects data:', error);
            Swal.fire('Error', 'Failed to load subjects data', 'error');
        }
    };

    // Pagination
    const goToPage = (page) => {
        setCurrentPage(page);
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pageNumbers = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return (
            <div className="flex justify-center mt-6">
                <nav className="flex space-x-2">
                    <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Previous
                    </button>
                    
                    {pageNumbers.map(number => (
                        <button
                            key={number}
                            onClick={() => goToPage(number)}
                            className={`px-3 py-2 text-sm font-medium border rounded-md ${
                                currentPage === number
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            {number}
                        </button>
                    ))}
                    
                    <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next
                    </button>
                </nav>
            </div>
        );
    };

    return (
        <DashboardLayout>
            <div className="p-6">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Batch Groups</h1>
                    <p className="mt-2 text-sm text-gray-600">
                        Manage your batch groups and their associated batches and subjects
                    </p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Total Groups</p>
                                <p className="text-2xl font-semibold text-gray-900">{stats.totalBatchGroups}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Controls */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div className="flex-1 max-w-md">
                        <input
                            type="text"
                            placeholder="Search batch groups..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    {loading && (
                        <div className="text-sm text-gray-500">Loading batch groups...</div>
                    )}
                    <button
                        onClick={() => openModal()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                        Add New Group
                    </button>
                </div>

                {error && (
                    <div className="mb-4 text-sm text-red-600">{error}</div>
                )}

                {/* Table */}
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Group Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    WhatsApp Group Name
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                                    {paginatedBatchGroups.length > 0 ? (
                                            paginatedBatchGroups.map((group) => (
                                    <tr key={group.batchGroupId} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {group.batchGroupId}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {group.name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {group.whatsappGroupName || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end space-x-2">
                                                <button
                                                    onClick={() => handleManageBatches(group)}
                                                    className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                                >
                                                    Batches
                                                </button>
                                                <button
                                                    onClick={() => handleManageSubjects(group)}
                                                    className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-purple-700 bg-purple-100 hover:bg-purple-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                                                >
                                                    Subjects
                                                </button>
                                                <button
                                                    onClick={() => openModal(group)}
                                                    className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(group)}
                                                    className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                                        No batch groups found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {renderPagination()}

                {/* Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                            <div className="mt-3">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    {editingBatchGroup ? 'Edit Batch Group' : 'Add New Batch Group'}
                                </h3>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Group Name
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleFormChange}
                                            required
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Enter group name"
                                        />
                                    </div>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            WhatsApp Group Name
                                        </label>
                                        <textarea
                                            name="whatsappGroupName"
                                            value={formData.whatsappGroupName}
                                            onChange={handleFormChange}
                                            rows={3}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="Enter WhatsApp group name"
                                        />
                                    </div>

                                    <div className="flex justify-end space-x-3 pt-4">
                                        <button
                                            type="button"
                                            onClick={closeModal}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            {editingBatchGroup ? 'Update' : 'Create'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Batches Management Modal */}
                {batchesModalOpen && selectedBatchGroup && (
                    <BatchesModal
                        isOpen={batchesModalOpen}
                        onClose={() => setBatchesModalOpen(false)}
                        batchGroup={selectedBatchGroup}
                        batchesData={batchesData}
                        onRefresh={async () => {
                            try {
                                const response = await axiosInstance.get(`${ADMIN_PREFIX}/batch-groups/${selectedBatchGroup.batchGroupId}/batches`, {
                                    params: { t: Date.now() },
                                });
                                const data = response.data || {};
                                setBatchesData(data);
                            } catch (error) {
                                console.error('Error refreshing batches data:', error);
                            }
                        }}
                    />
                )}

                {/* Subjects Management Modal */}
                {subjectsModalOpen && selectedBatchGroup && (
                    <SubjectsModal
                        isOpen={subjectsModalOpen}
                        onClose={() => setSubjectsModalOpen(false)}
                        batchGroup={selectedBatchGroup}
                        subjectsData={subjectsData}
                        onRefresh={async () => {
                            try {
                                const response = await axiosInstance.get(`${ADMIN_PREFIX}/batch-groups/${selectedBatchGroup.batchGroupId}/subjects`, {
                                    params: { t: Date.now() },
                                });
                                const data = response.data || {};
                                setSubjectsData(data);
                            } catch (error) {
                                console.error('Error refreshing subjects data:', error);
                            }
                        }}
                    />
                )}
            </div>
        </DashboardLayout>
    );
};

// Batches Modal Component
const BatchesModal = ({ isOpen, onClose, batchGroup, batchesData, onRefresh }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBatches, setSelectedBatches] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [localBatchesData, setLocalBatchesData] = useState(batchesData);

    // Update local state when props change
    React.useEffect(() => {
        console.log('BatchesModal: batchesData prop changed:', batchesData);
        setLocalBatchesData(batchesData);
    }, [batchesData]);

    const { assignedBatches = [], availableBatches = [] } = localBatchesData;

    // Filter logic (null-safe)
    const filteredAssignedBatches = useMemo(() => {
        const term = (searchTerm || '').toLowerCase();
        if (!term) return assignedBatches;

        return assignedBatches.filter((batch) => {
            const name = (batch && batch.batchName) ? String(batch.batchName).toLowerCase() : '';
            const courseName = (batch && batch.course && batch.course.fullName)
                ? String(batch.course.fullName).toLowerCase()
                : '';
            return name.includes(term) || courseName.includes(term);
        });
    }, [assignedBatches, searchTerm]);

    // Prepare options for react-select
    const batchOptions = availableBatches.map((batch) => ({
        value: batch.batchId,
        label: `${batch.batchName} - ${batch.course?.fullName || 'Unknown Course'}`
    }));

    // Handlers
    const handleAddBatches = async () => {
        if (selectedBatches.length === 0) {
            Swal.fire('Error', 'Please select at least one batch to add', 'error');
            return;
        }

        try {
            const response = await axiosInstance.post(`${ADMIN_PREFIX}/batch-groups/${batchGroup.batchGroupId}/batches/assign`, {
                batchIds: selectedBatches,
            });

            const result = response.data || {};
            
            if (result.success) {
                setIsAddModalOpen(false);
                setSelectedBatches([]);
                setSearchTerm('');
                console.log('Assign result:', result);
                // 1) Immediate optimistic update for snappy UX (source of truth still server)
                setLocalBatchesData((prev) => {
                    const prevAssigned = Array.isArray(prev.assignedBatches) ? prev.assignedBatches : [];
                    const prevAvailable = Array.isArray(prev.availableBatches) ? prev.availableBatches : [];
                    const newlyAssigned = prevAvailable.filter((b) => selectedBatches.includes(b.batchId));
                    const remainingAvailable = prevAvailable.filter((b) => !selectedBatches.includes(b.batchId));
                    // If server provided the definitive lists, prefer them
                    const assigned = result.assignedBatches ?? [...prevAssigned, ...newlyAssigned];
                    const available = result.availableBatches ?? remainingAvailable;
                    return { ...prev, assignedBatches: assigned, availableBatches: available };
                });

                // 2) Refresh from server to guarantee consistency
                await onRefresh();
                Swal.fire('Success', 'Batches assigned successfully', 'success');
            } else {
                Swal.fire('Error', result.message || 'Failed to add batches to group', 'error');
            }
        } catch (error) {
            console.error('Error adding batches:', error);
            Swal.fire('Error', 'Failed to add batches to group', 'error');
        }
    };

    const handleRemoveBatch = async (batchId, batchName) => {
        const result = await Swal.fire({
            title: 'Remove Batch?',
            text: `Are you sure you want to remove "${batchName}" from this group?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, remove it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await axiosInstance.delete(`${ADMIN_PREFIX}/batch-groups/${batchGroup.batchGroupId}/batches/${batchId}`);

                const deleteResult = response.data || {};
                
                if (deleteResult.success) {
                    // Optimistically update local lists
                    setLocalBatchesData((prev) => {
                        const prevAssigned = Array.isArray(prev.assignedBatches) ? prev.assignedBatches : [];
                        const removed = prevAssigned.find((b) => b.batchId === batchId);
                        const nextAssigned = prevAssigned.filter((b) => b.batchId !== batchId);
                        const nextAvailable = removed ? [...(prev.availableBatches || []), removed] : (prev.availableBatches || []);
                        return { ...prev, assignedBatches: nextAssigned, availableBatches: nextAvailable };
                    });
                    // And refresh from server to be safe
                    await onRefresh();
                    Swal.fire('Success', 'Batch removed successfully', 'success');
                } else {
                    Swal.fire('Error', deleteResult.message || 'Failed to remove batch from group', 'error');
                }
            } catch (error) {
                console.error('Error removing batch:', error);
                Swal.fire('Error', 'Failed to remove batch from group', 'error');
            }
        }
    };

    const handleBatchSelection = (selectedOptions) => {
        const ids = selectedOptions ? selectedOptions.map((option) => option.value) : [];
        setSelectedBatches(ids);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-4 mx-auto p-5 border max-w-6xl shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900">
                        Manage Batches - {batchGroup.name}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Assigned Batches</p>
                                <p className="text-2xl font-semibold text-gray-900">{assignedBatches.length}</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-green-50 rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Available Batches</p>
                                <p className="text-2xl font-semibold text-gray-900">{availableBatches.length}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Controls */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div className="flex-1 max-w-md">
                        <input
                            type="text"
                            placeholder="Search assigned batches..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                        Add Batches
                    </button>
                </div>

                {/* Table */}
                <div className="bg-white shadow rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Batch ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Batch Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Course
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredAssignedBatches.length > 0 ? (
                                filteredAssignedBatches.map((batch) => (
                                    <tr key={batch.batchId} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {batch.batchId}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {batch.batchName}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {batch.course?.fullName || 'Unknown Course'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => handleRemoveBatch(batch.batchId, batch.batchName)}
                                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                            >
                                                Remove
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                                        No batches assigned to this group.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Add Batches Modal */}
                {isAddModalOpen && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-60">
                        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                            <div className="mt-3">
                                <h3 className="text-lg font-medium text-gray-900 mb-4">
                                    Add Batches to Group
                                </h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Select Batches
                                        </label>
                                        <Select
                                            isMulti
                                            options={batchOptions}
                                            value={batchOptions.filter((option) => selectedBatches.includes(option.value))}
                                            onChange={handleBatchSelection}
                                            placeholder="Choose batches to add..."
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                            menuShouldBlockScroll
                                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                        />
                                    </div>

                                    <div className="flex justify-end space-x-3 pt-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsAddModalOpen(false);
                                                setSelectedBatches([]);
                                            }}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleAddBatches}
                                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            Add Selected Batches
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};


// Subjects Modal Component
const SubjectsModal = ({ isOpen, onClose, batchGroup, subjectsData, onRefresh }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBatchSubjects, setSelectedBatchSubjects] = useState([]);
    const [localSubjectsData, setLocalSubjectsData] = useState(subjectsData);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [selectedBatchId, setSelectedBatchId] = useState(null);

    // Update local state when props change
    useEffect(() => {
        setLocalSubjectsData(subjectsData);
    }, [subjectsData]);

    const { assignedBatchSubjects = [], availableBatchSubjects = [] } = localSubjectsData;

    const assignedBatchSubjectIdSet = useMemo(() => {
        const ids = new Set();
        (Array.isArray(assignedBatchSubjects) ? assignedBatchSubjects : []).forEach((batchSubject) => {
            if (batchSubject?.batchSubjectId) {
                ids.add(batchSubject.batchSubjectId);
            }
        });
        return ids;
    }, [assignedBatchSubjects]);

    const filteredAvailableBatchSubjects = useMemo(() => {
        if (!Array.isArray(availableBatchSubjects)) return [];
        return availableBatchSubjects.filter((batchSubject) => batchSubject?.batchSubjectId && !assignedBatchSubjectIdSet.has(batchSubject.batchSubjectId));
    }, [availableBatchSubjects, assignedBatchSubjectIdSet]);

    // Filter logic (null-safe, matches backend shape: subject + batch nested)
    const filteredAssignedBatchSubjects = useMemo(() => {
        const term = (searchTerm || '').toLowerCase();
        if (!term) return assignedBatchSubjects;

        return assignedBatchSubjects.filter((batchSubject) => {
            const fullName = (batchSubject && batchSubject.subject && batchSubject.subject.fullName)
                ? String(batchSubject.subject.fullName).toLowerCase()
                : '';
            const shortName = (batchSubject && batchSubject.subject && batchSubject.subject.shortName)
                ? String(batchSubject.subject.shortName).toLowerCase()
                : '';
            const batchName = (batchSubject && batchSubject.batch && batchSubject.batch.batchName)
                ? String(batchSubject.batch.batchName).toLowerCase()
                : '';
            return (
                fullName.includes(term) ||
                shortName.includes(term) ||
                batchName.includes(term)
            );
        });
    }, [assignedBatchSubjects, searchTerm]);

    // Batch picklist derived from available subjects (dedupe by batch)
    const batchOptions = useMemo(() => {
        const optionsMap = new Map();
        filteredAvailableBatchSubjects.forEach((batchSubject) => {
            const batch = batchSubject?.batch;
            if (!batch || !batch.batchId || optionsMap.has(batch.batchId)) {
                return;
            }

            const whatsAppName = batch.whatsappGroupName
                || batch.batchGroupWhatsappName
                || batch.batchGroup?.whatsappGroupName
                || batch.whatsapp_group_name
                || 'WhatsApp N/A';
            const displayBatchName = batch.batchName || batch.batch_name || 'Unnamed Batch';

            const label = `${displayBatchName} - WhatsApp: ${whatsAppName}`;

            optionsMap.set(batch.batchId, {
                value: batch.batchId,
                label,
                batch: {
                    ...batch,
                    resolvedWhatsappGroupName: whatsAppName,
                },
            });
        });
        return Array.from(optionsMap.values());
    }, [availableBatchSubjects]);

    // Subject picklist grouped by batch (for fast lookups + filtering)
    const batchSubjectOptionsByBatchId = useMemo(() => {
        const grouped = new Map();
        filteredAvailableBatchSubjects.forEach((batchSubject) => {
            const batchId = batchSubject?.batch?.batchId;
            if (!batchId || !batchSubject?.batchSubjectId) return;

            const label = batchSubject.customSubjectName
                || `${batchSubject.subject?.shortName || 'Code N/A'} - ${batchSubject.subject?.fullName || 'Subject N/A'}`;

            const option = {
                value: batchSubject.batchSubjectId,
                label,
                batchId,
            };

            if (!grouped.has(batchId)) {
                grouped.set(batchId, []);
            }
            grouped.get(batchId).push(option);
        });
        return grouped;
    }, [filteredAvailableBatchSubjects]);

    const selectedBatchOption = useMemo(() => batchOptions.find((option) => option.value === selectedBatchId) || null, [batchOptions, selectedBatchId]);

    const subjectOptionsForSelectedBatch = useMemo(() => {
        if (!selectedBatchId) return [];
        const options = batchSubjectOptionsByBatchId.get(selectedBatchId) || [];
        return options.filter((option) => !selectedBatchSubjects.includes(option.value));
    }, [batchSubjectOptionsByBatchId, selectedBatchId, selectedBatchSubjects]);

    const selectedSubjectOptionsForBatch = useMemo(() => {
        if (!selectedBatchId) return [];
        const options = batchSubjectOptionsByBatchId.get(selectedBatchId) || [];
        return options.filter((option) => selectedBatchSubjects.includes(option.value));
    }, [batchSubjectOptionsByBatchId, selectedBatchId, selectedBatchSubjects]);

    const combinedSubjectOptions = useMemo(() => {
        if (!selectedBatchId) return [];
        return [...selectedSubjectOptionsForBatch, ...subjectOptionsForSelectedBatch];
    }, [selectedBatchId, selectedSubjectOptionsForBatch, subjectOptionsForSelectedBatch]);

    // Handlers
    const handleAddBatchSubjects = async () => {
        if (selectedBatchSubjects.length === 0) {
            Swal.fire('Error', 'Please select at least one batch subject to add', 'error');
            return;
        }

        try {
            const response = await axiosInstance.post(`${ADMIN_PREFIX}/batch-groups/${batchGroup.batchGroupId}/subjects/assign`, {
                batchSubjectIds: selectedBatchSubjects,
            });

            const result = response.data || {};
            
            if (result.success) {
                setIsAddModalOpen(false);
                setSelectedBatchSubjects([]);
                setSelectedBatchId(null);
                onRefresh();
                Swal.fire('Success', 'Batch subjects assigned successfully', 'success');
            } else {
                Swal.fire('Error', result.message || 'Failed to add batch subjects to group', 'error');
            }
        } catch (error) {
            console.error('Error adding batch subjects:', error);
            Swal.fire('Error', 'Failed to add batch subjects to group', 'error');
        }
    };

    const handleRemoveBatchSubject = async (batchSubjectId, subjectName) => {
        const result = await Swal.fire({
            title: 'Remove Batch Subject?',
            text: `Are you sure you want to remove "${subjectName}" from this group?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, remove it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await axiosInstance.delete(`${ADMIN_PREFIX}/batch-groups/${batchGroup.batchGroupId}/subjects/${batchSubjectId}`);

                const deleteResult = response.data || {};
                
                if (deleteResult.success) {
                    onRefresh();
                    Swal.fire('Success', 'Batch subject removed successfully', 'success');
                } else {
                    Swal.fire('Error', deleteResult.message || 'Failed to remove batch subject from group', 'error');
                }
            } catch (error) {
                console.error('Error removing batch subject:', error);
                Swal.fire('Error', 'Failed to remove batch subject from group', 'error');
            }
        }
    };

    const handleBatchSubjectSelection = (selectedOptions) => {
        const ids = selectedOptions ? selectedOptions.map((option) => option.value) : [];
        setSelectedBatchSubjects(ids);
    };

    const handleBatchSelection = (selectedOption) => {
        setSelectedBatchId(selectedOption ? selectedOption.value : null);
        setSelectedBatchSubjects([]);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-4 mx-auto p-5 border max-w-6xl shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900">
                        Manage Subjects - {batchGroup.name}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-purple-50 rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Assigned Batch Subjects</p>
                                <p className="text-2xl font-semibold text-gray-900">{assignedBatchSubjects.length}</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="bg-green-50 rounded-lg p-4">
                        <div className="flex items-center">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                            </div>
                            <div className="ml-4">
                                <p className="text-sm font-medium text-gray-600">Available Batch Subjects</p>
                                <p className="text-2xl font-semibold text-gray-900">{availableBatchSubjects.length}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Controls */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div className="flex-1 max-w-md">
                        <input
                            type="text"
                            placeholder="Search assigned batch subjects..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                    <button
                        onClick={() => setIsAddModalOpen(true)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                    >
                        Add Batch Subjects
                    </button>
                </div>

                {/* Table */}
                <div className="bg-white shadow rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50 sticky top-0">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Batch Subject ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Batch Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Subject Name
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredAssignedBatchSubjects.length > 0 ? (
                                filteredAssignedBatchSubjects.map((batchSubject) => (
                                    <tr key={batchSubject.batchSubjectId} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {batchSubject.batchSubjectId}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {batchSubject.batch?.batchName || 'Unknown Batch'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {batchSubject.customSubjectName || `${batchSubject.subject?.shortName || 'Unknown Code'} - ${batchSubject.subject?.fullName || 'Unknown Subject'}`}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => handleRemoveBatchSubject(batchSubject.batchSubjectId, batchSubject.customSubjectName || batchSubject.shortName)}
                                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                            >
                                                Remove
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                                        No batch subjects assigned to this group.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Add Batch Subjects Modal */}
                {isAddModalOpen && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-60">
                        <div className="relative top-16 mx-auto p-6 border w-full max-w-3xl shadow-lg rounded-md bg-white">
                            <div className="mt-1">
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                                    Add Batch Subjects to Group
                                </h3>
                                <div className="space-y-4">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Choose Batch
                                            </label>
                                            {filteredAvailableBatchSubjects.length === 0 && (
                                                <p className="text-sm text-gray-500 mb-2">
                                                    No selectable subjects left. Add batches to this group first or remove already assigned subjects to free up options.
                                                </p>
                                            )}
                                            <Select
                                                options={batchOptions}
                                                value={selectedBatchOption}
                                                onChange={handleBatchSelection}
                                                placeholder="Select a batch to view subjects..."
                                                isClearable
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                menuShouldBlockScroll
                                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                noOptionsMessage={() => 'No batches available'}
                                            />
                                            {selectedBatchOption?.batch && (
                                                <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                                                    <p className="font-semibold text-gray-900">Batch Summary</p>
                                                    <p><span className="text-gray-500">Name:</span> {selectedBatchOption.batch.batchName || selectedBatchOption.batch.batch_name || 'N/A'}</p>
                                                    <p><span className="text-gray-500">WhatsApp:</span> {selectedBatchOption.batch.resolvedWhatsappGroupName || selectedBatchOption.batch.whatsappGroupName || selectedBatchOption.batch.whatsapp_group_name || 'N/A'}</p>
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Select Subject Names
                                            </label>
                                            <Select
                                                isMulti
                                                isDisabled={!selectedBatchId}
                                                options={combinedSubjectOptions}
                                                value={selectedSubjectOptionsForBatch}
                                                onChange={handleBatchSubjectSelection}
                                                placeholder={selectedBatchId ? 'Choose subjects to add...' : 'Select a batch first'}
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                                menuShouldBlockScroll
                                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                                noOptionsMessage={() => selectedBatchId ? 'All subjects already selected' : 'Pick a batch to load subjects'}
                                            />
                                            {selectedBatchId && combinedSubjectOptions.length === 0 && (
                                                <p className="mt-2 text-sm text-gray-500">
                                                    All available subjects for this batch are already assigned to the group.
                                                </p>
                                            )}
                                            {selectedBatchSubjects.length > 0 && (
                                                <p className="mt-2 text-sm text-green-700">
                                                    {selectedBatchSubjects.length} subject{selectedBatchSubjects.length > 1 ? 's' : ''} selected for {selectedBatchOption?.batch?.batchName || 'this batch'}.
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex justify-end space-x-3 pt-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsAddModalOpen(false);
                                                setSelectedBatchSubjects([]);
                                                setSelectedBatchId(null);
                                            }}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleAddBatchSubjects}
                                            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        >
                                            Add Selected Batch Subjects
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BatchGroups;
